/**
 * ReadingPane
 *
 * Displays the selected message or thread in a split-view pane.
 * Renders ThreadView for multi-message threads, MessageView for single messages.
 * Includes ComposeDialog triggered by reply/forward actions.
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Skeleton } from '@/components/ui/skeleton';
import { Mail } from 'lucide-react';
import { MessageView } from './MessageView';
import { ThreadView } from './ThreadView';
import { ComposeDialog } from './ComposeDialog';
import type { ComposeMode } from '../hooks/useCompose';
import type { EmailMessage, EmailMessageDetail } from '@/lib/api';

interface ReadingPaneProps {
  messageId: string | null;
  accountId: string | undefined;
}

export function ReadingPane({ messageId, accountId }: ReadingPaneProps) {
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState<ComposeMode>('new');
  const [composeReplyMessage, setComposeReplyMessage] = useState<
    EmailMessage | EmailMessageDetail | undefined
  >(undefined);

  const { data: message, isLoading } = useQuery({
    queryKey: queryKeys.email.messageDetail(messageId ?? ''),
    queryFn: async () => {
      if (!messageId || !accountId) throw new Error('Missing params');
      return api.getEmailMessage(messageId, accountId);
    },
    enabled: !!messageId && !!accountId,
  });

  const openCompose = useCallback((mode: ComposeMode, msg?: EmailMessage | EmailMessageDetail) => {
    setComposeMode(mode);
    setComposeReplyMessage(msg);
    setComposeOpen(true);
  }, []);

  if (!messageId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Mail className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm">Select a message to read</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
        <div className="mt-6 space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Message not found</p>
      </div>
    );
  }

  // If message belongs to a thread with multiple messages, show ThreadView
  const isThread = message.threadId;

  return (
    <>
      <div className="h-full overflow-y-auto">
        {isThread ? (
          <ThreadView
            threadId={message.threadId}
            accountId={accountId!}
            onReply={(msg) => openCompose('reply', msg)}
            onReplyAll={(msg) => openCompose('replyAll', msg)}
            onForward={(msg) => openCompose('forward', msg)}
          />
        ) : (
          <MessageView
            message={message}
            onReply={() => openCompose('reply', message)}
            onReplyAll={() => openCompose('replyAll', message)}
            onForward={() => openCompose('forward', message)}
          />
        )}
      </div>

      <ComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        accountId={accountId}
        mode={composeMode}
        replyToMessage={composeReplyMessage}
      />
    </>
  );
}
